import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Sparkles, Award, Zap, Compass, HeartHandshake, CheckCircle2, ShieldCheck,
  Quote, Star, ArrowRight, TrendingUp, Building, Users, MessageSquare,
  Download, FileText, Megaphone, Target, Crown, Calculator, BarChart3,
  FileCheck2, Calendar, Coins, AlertCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { Zap, Award, Compass, HeartHandshake, CheckCircle2, ShieldCheck,
  Crown, Calculator, Users, TrendingUp, BarChart3, FileCheck2, Calendar, Coins };

const fmt = (v) => v == null ? '—' : `$${Math.round(v).toLocaleString()}`;
const fmtCompact = (v) => {
  if (v == null) return '—';
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${Math.round(v)}`;
};

// ── Sub-components ─────────────────────────────────────────────────────────
const ClaimTile = ({ metric, label, context, large = false }) => (
  <div className={`bg-white border border-[#E5E5E5] rounded-[12px] p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] ${large ? 'lg:col-span-2' : ''}`}>
    <p className={`font-bold text-[#171717] font-heading leading-none ${large ? 'text-5xl' : 'text-3xl'}`}>{metric}</p>
    <p className="text-[11px] font-semibold uppercase tracking-wider text-[#171717] mt-3">{label}</p>
    <p className="text-xs text-[#737373] mt-1 leading-relaxed">{context}</p>
  </div>
);

const Pillar = ({ pillar }) => {
  const Icon = ICONS[pillar.icon] || Sparkles;
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-[12px] p-6 shadow-[0_2px_10px_rgba(0,0,0,0.02)] flex flex-col">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="w-10 h-10 rounded-[10px] bg-[#171717] flex items-center justify-center">
          <Icon className="w-5 h-5 text-white" strokeWidth={1.5} />
        </div>
        <div className="text-right">
          <p className="text-xs font-mono font-bold text-[#171717]">{pillar.mentionCount}</p>
          <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3] font-semibold">mentions</p>
        </div>
      </div>
      <h3 className="text-base font-semibold text-[#171717] font-heading">{pillar.label}</h3>
      <p className="text-xs text-[#525252] mt-1 leading-relaxed">{pillar.tagline}</p>
      <div className="my-4 border-t border-[#F0F0F0]" />
      <p className="text-[10px] uppercase tracking-wider font-bold text-[#A3A3A3] mb-2">Sales Pitch</p>
      <p className="text-[11px] text-[#525252] leading-relaxed italic mb-3">{pillar.salesPitch}</p>
      {pillar.topQuotes.length > 0 && (
        <>
          <p className="text-[10px] uppercase tracking-wider font-bold text-[#A3A3A3] mb-2 mt-auto">Top Quote</p>
          <blockquote className="border-l-2 border-[#171717] pl-3 py-1">
            <p className="text-[11px] text-[#525252] italic leading-relaxed">"{pillar.topQuotes[0].comment}"</p>
            <p className="text-[10px] text-[#737373] mt-1.5 font-mono">
              — {pillar.topQuotes[0].propertyName || pillar.topQuotes[0].planNumber} · NPS {pillar.topQuotes[0].score}
            </p>
          </blockquote>
        </>
      )}
    </div>
  );
};

const CaseStudyCard = ({ study }) => {
  const angleStyles = {
    'Anchor Client': 'bg-emerald-50 border-emerald-200 text-emerald-700',
    'Complex Portfolio': 'bg-blue-50 border-blue-200 text-blue-700',
    'Brand Advocate': 'bg-amber-50 border-amber-200 text-amber-700',
  };
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-[12px] p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
      <div className="flex items-start justify-between gap-3 mb-2">
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${angleStyles[study.angle] || 'bg-[#F5F5F5] border-[#E5E5E5] text-[#525252]'}`}>
          {study.angle}
        </span>
        <span className="text-[10px] font-mono text-[#A3A3A3]">{study.basePlan}</span>
      </div>
      <h3 className="text-base font-semibold text-[#171717] font-heading">{study.propertyName}</h3>
      <p className="text-xs text-[#525252] mt-1">{study.headline}</p>
      <div className="grid grid-cols-4 gap-2 my-4 py-3 border-y border-[#F0F0F0]">
        <div>
          <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3] font-semibold">NPS</p>
          <p className="text-sm font-bold text-[#171717] font-mono">{study.avgNPS ? study.avgNPS.toFixed(1) : '—'}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3] font-semibold">Revenue</p>
          <p className="text-sm font-bold text-[#171717] font-mono">{fmtCompact(study.revenue)}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3] font-semibold">Margin</p>
          <p className="text-sm font-bold text-[#171717] font-mono">{study.margin ? `${study.margin.toFixed(0)}%` : '—'}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3] font-semibold">Lots</p>
          <p className="text-sm font-bold text-[#171717] font-mono">{study.lots}</p>
        </div>
      </div>
      <p className="text-[11px] text-[#737373] leading-relaxed">{study.why}</p>
      {study.comments?.length > 0 && (
        <blockquote className="mt-4 border-l-2 border-[#171717] pl-3 py-1">
          <p className="text-[11px] text-[#525252] italic leading-relaxed">"{study.comments[0].comment}"</p>
          <p className="text-[10px] text-[#737373] mt-1 font-mono">— Plan {study.comments[0].planNumber} · Score {study.comments[0].score}</p>
        </blockquote>
      )}
    </div>
  );
};

const QuoteCard = ({ q }) => (
  <div className="bg-white border border-[#E5E5E5] rounded-[10px] p-4 shadow-[0_2px_10px_rgba(0,0,0,0.02)] break-inside-avoid mb-3">
    <Quote className="w-3.5 h-3.5 text-[#A3A3A3] mb-1.5" strokeWidth={1.5} />
    <p className="text-xs text-[#171717] italic leading-relaxed">"{q.comment}"</p>
    <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#F0F0F0]">
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold text-[#171717] truncate">{q.propertyName || q.planNumber}</p>
        <p className="text-[9px] text-[#A3A3A3] font-mono">{q.manager} · NPS {q.score}</p>
      </div>
      {q.themes && q.themes.length > 0 && (
        <span className="text-[9px] font-semibold text-[#171717] bg-[#F5F5F5] px-1.5 py-0.5 rounded border border-[#E5E5E5] flex-shrink-0 ml-2">
          {q.themes.length} {q.themes.length === 1 ? 'theme' : 'themes'}
        </span>
      )}
    </div>
  </div>
);

const CounterRow = ({ item }) => (
  <div className="grid grid-cols-12 gap-4 items-center py-3 border-b border-[#F0F0F0] last:border-0">
    <div className="col-span-5">
      <p className="text-xs font-semibold text-[#171717]">{item.pain}</p>
      <p className="text-[10px] text-[#A3A3A3]">{item.mentionCount} mention{item.mentionCount > 1 ? 's' : ''} flagged industry-wide</p>
    </div>
    <div className="col-span-2 flex justify-center">
      <ArrowRight className="w-4 h-4 text-[#A3A3A3]" strokeWidth={1.5} />
    </div>
    <div className="col-span-5">
      {item.proofPillar ? (
        <>
          <p className="text-xs font-semibold text-emerald-700">{item.proofPillar}</p>
          <p className="text-[10px] text-[#A3A3A3]">{item.proofCount} positive mentions in our promoter set</p>
        </>
      ) : (
        <p className="text-xs text-[#A3A3A3]">—</p>
      )}
    </div>
  </div>
);

// ── Committee Experience subcomponents ──────────────────────────────────
const PersonaCard = ({ persona }) => {
  const Icon = ICONS[persona.icon] || Users;
  const ev = persona.evidence || {};
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-[12px] p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-[10px] bg-[#171717] flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-white" strokeWidth={1.5} />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-[#171717] font-heading">{persona.role}</h3>
          <p className="text-xs text-[#525252] mt-1 leading-relaxed">{persona.jobToBeDone}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 my-4 py-3 border-y border-[#F0F0F0]">
        <div>
          <p className="text-[9px] uppercase tracking-wider text-emerald-600 font-semibold">Promoter mentions</p>
          <p className="text-lg font-bold text-emerald-600 font-mono">{ev.promoterMentions ?? 0}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase tracking-wider text-red-600 font-semibold">Detractor mentions</p>
          <p className="text-lg font-bold text-red-600 font-mono">{ev.detractorMentions ?? 0}</p>
        </div>
      </div>
      <div className="space-y-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-bold text-[#A3A3A3] mb-1.5">What they need</p>
          <ul className="space-y-1">
            {persona.needs.map((n) => (
              <li key={n} className="text-[11px] text-[#525252] flex items-start gap-1.5 leading-relaxed">
                <CheckCircle2 className="w-3 h-3 text-emerald-600 flex-shrink-0 mt-0.5" strokeWidth={2} /> <span>{n}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider font-bold text-[#A3A3A3] mb-1.5">Their pain</p>
          <ul className="space-y-1">
            {persona.pains.map((p) => (
              <li key={p} className="text-[11px] text-[#525252] flex items-start gap-1.5 leading-relaxed">
                <AlertCircle className="w-3 h-3 text-red-500 flex-shrink-0 mt-0.5" strokeWidth={2} /> <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      {ev.topProof && ev.topProof.length > 0 && (
        <blockquote className="mt-4 border-l-2 border-emerald-500 pl-3 py-1 bg-emerald-50/30 rounded-r">
          <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-1">Proof we deliver</p>
          <p className="text-[11px] text-[#525252] italic leading-relaxed">"{ev.topProof[0].comment}"</p>
          <p className="text-[10px] text-[#737373] mt-1.5 font-mono">— {ev.topProof[0].propertyName || ev.topProof[0].planNumber} · NPS {ev.topProof[0].score}</p>
        </blockquote>
      )}
    </div>
  );
};

const FinancialPillarCard = ({ pillar }) => {
  const Icon = ICONS[pillar.icon] || TrendingUp;
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-[12px] p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-[#171717]" strokeWidth={1.5} />
        <h4 className="text-sm font-semibold text-[#171717] font-heading">{pillar.label}</h4>
      </div>
      <p className="text-3xl font-bold text-[#171717] font-heading leading-none">{pillar.metric}</p>
      <p className="text-[11px] text-[#737373] mt-2 leading-relaxed">{pillar.subline}</p>
      <div className="my-3 border-t border-[#F0F0F0]" />
      <p className="text-[11px] text-[#525252] italic leading-relaxed">{pillar.narrative}</p>
    </div>
  );
};

const CommitteeExperienceSection = ({ ce }) => (
  <section className="space-y-6">
    {/* Hero */}
    <div className="bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white rounded-[16px] p-8 lg:p-10 shadow-xl">
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-4 h-4 opacity-70" strokeWidth={1.5} />
        <p className="text-[11px] font-semibold uppercase tracking-wider opacity-70">Committee Experience</p>
      </div>
      <h2 className="text-2xl lg:text-4xl font-bold font-heading tracking-tight mb-3">{ce.headline}</h2>
      <p className="text-sm text-white/70 leading-relaxed max-w-3xl">{ce.subline}</p>
    </div>

    {/* Personas */}
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Crown className="w-4 h-4 text-[#171717]" strokeWidth={1.5} />
        <h3 className="text-base font-semibold uppercase tracking-wider text-[#171717]">Who Sits on the Committee · Their Job, Need & Pain</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {ce.personas.map((p) => (
          <PersonaCard key={p.key} persona={p} />
        ))}
      </div>
    </div>

    {/* Financial Expertise */}
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Calculator className="w-4 h-4 text-[#171717]" strokeWidth={1.5} />
        <h3 className="text-base font-semibold uppercase tracking-wider text-[#171717]">Financial Expertise · The Moat</h3>
      </div>
      <p className="text-xs text-[#737373] mb-4 max-w-3xl leading-relaxed">
        Strata is a regulated financial environment. Committees carry signing authority on hundreds of thousands of dollars annually — and the cost of bad financial advice is much higher than the management fee. This is where we differentiate.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ce.financialPillars.map((fp) => (
          <FinancialPillarCard key={fp.key} pillar={fp} />
        ))}
      </div>
    </div>
  </section>
);

// ── Main Component ─────────────────────────────────────────────────────────
const TKValueProposition = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/value-proposition`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load value proposition:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-3 border-[#E5E5E5] border-t-[#171717] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="value-proposition-view">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#171717] to-[#262626] text-white rounded-[16px] p-8 lg:p-12 shadow-xl">
        <div className="flex items-center gap-2 mb-4">
          <Megaphone className="w-4 h-4 opacity-70" strokeWidth={1.5} />
          <p className="text-[11px] font-semibold uppercase tracking-wider opacity-70">TK Value Proposition</p>
        </div>
        <h1 className="text-3xl lg:text-5xl font-bold font-heading tracking-tight mb-3">
          {data.headline.tagline}
        </h1>
        <p className="text-base text-white/70 leading-relaxed max-w-3xl">{data.headline.subline}</p>
        <div className="flex flex-wrap gap-4 mt-8 pt-8 border-t border-white/10">
          <div>
            <p className="text-3xl font-bold font-heading">20,000+</p>
            <p className="text-[10px] uppercase tracking-wider opacity-60">active lots managed</p>
          </div>
          <div className="w-px bg-white/10" />
          <div>
            <p className="text-3xl font-bold font-heading">300+</p>
            <p className="text-[10px] uppercase tracking-wider opacity-60">OC plans</p>
          </div>
          <div className="w-px bg-white/10" />
          <div>
            <p className="text-3xl font-bold font-heading">{data.kpis.ratedSevenPlusPct ?? data.kpis.promoterPct}%</p>
            <p className="text-[10px] uppercase tracking-wider opacity-60">rate us 7+ out of 10</p>
          </div>
          <div className="w-px bg-white/10" />
          <div>
            <p className="text-3xl font-bold font-heading">{data.kpis.enps > 0 ? '+' : ''}{data.kpis.enps}</p>
            <p className="text-[10px] uppercase tracking-wider opacity-60">eNPS</p>
          </div>
          <div className="w-px bg-white/10" />
          <div>
            <p className="text-3xl font-bold font-heading">{data.kpis.totalManagers}</p>
            <p className="text-[10px] uppercase tracking-wider opacity-60">specialist managers</p>
          </div>
        </div>
      </div>

      {/* Marketing Claims */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Target className="w-4 h-4 text-[#171717]" strokeWidth={1.5} />
          <h2 className="text-base font-semibold uppercase tracking-wider text-[#171717]">Headline Claims</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {data.claims.map((c) => (
            <ClaimTile key={c.label} metric={c.metric} label={c.label} context={c.context} />
          ))}
        </div>
      </section>

      {/* Value Pillars */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-[#171717]" strokeWidth={1.5} />
          <h2 className="text-base font-semibold uppercase tracking-wider text-[#171717]">Value Pillars · evidence from {data.topQuotes.length} promoter testimonials</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.pillars.map((p) => (
            <Pillar key={p.key} pillar={p} />
          ))}
        </div>
      </section>

      {/* Case Studies */}
      {data.caseStudies.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Star className="w-4 h-4 text-[#171717]" strokeWidth={1.5} />
            <h2 className="text-base font-semibold uppercase tracking-wider text-[#171717]">Featured Client Stories</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.caseStudies.map((c) => (
              <CaseStudyCard key={c.basePlan} study={c} />
            ))}
          </div>
        </section>
      )}

      {/* Counter-narrative */}
      {data.counterNarrative.length > 0 && (
        <section className="bg-white border border-[#E5E5E5] rounded-[12px] p-6 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-4 h-4 text-[#171717]" strokeWidth={1.5} />
            <h2 className="text-base font-semibold text-[#171717] font-heading">Counter-Narrative · Industry Pain → Our Proof Point</h2>
          </div>
          <p className="text-xs text-[#737373] mb-5">What clients complain about when discussing incumbent strata services, paired with evidence we don't have that gap.</p>
          {data.counterNarrative.map((item) => (
            <CounterRow key={item.pain} item={item} />
          ))}
        </section>
      )}

      {/* Committee Experience */}
      {data.committeeExperience && <CommitteeExperienceSection ce={data.committeeExperience} />}

      {/* Quote wall */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Quote className="w-4 h-4 text-[#171717]" strokeWidth={1.5} />
          <h2 className="text-base font-semibold uppercase tracking-wider text-[#171717]">Verbatim from the Front Line</h2>
        </div>
        <div className="columns-1 md:columns-2 lg:columns-3 gap-4">
          {data.topQuotes.map((q) => (
            <QuoteCard key={`${q.planNumber}-${q.comment.slice(0, 20)}`} q={q} />
          ))}
        </div>
      </section>
    </div>
  );
};

export default TKValueProposition;
